import { TitleCasePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChange, SimpleChanges } from '@angular/core';
import { FILE_CLAIM_CONFIG } from 'app/claims/components/file-claim/config/file-claim.config';
import { ClaimStatus } from 'app/claims/models/claim-status.enum';
import { ClaimService } from 'app/claims/services/claim.service';
import { User } from 'app/shared/services/user/models/user.model';
import { EnumService } from 'app/shared/services/utility/enum.service';
import { Package } from 'app/ship/models/package.model';
import { filter, Observable, of, Subject } from 'rxjs';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { IShipmentPackage } from '../../models/shipments/shipment-package.interface';
import { ITrackingScan } from '../tracking-scans/dialog/models/tracking-scan.interface';
import { TrackingScanService } from '../tracking-scans/services/tracking-scan.service';
import { Customer } from 'app/shared/models/customer/customer.model';
import { AppState } from 'app/app.state';

@Component({
    selector: 'upsc-ship-details-actions',
    templateUrl: './ship-details-actions.component.html',
    styleUrls: ['./ship-details-actions.component.scss'],
    providers: [
        ClaimService,
        TitleCasePipe,
    ],
})
export class ShipDetailsActionsComponent implements OnDestroy, OnChanges {
    @Input() public showReship = true;
    @Input() public showReprintLabel = true;
    @Input() public showEmailLabel = true;
    @Input() public showDownloadLabel = true;
    @Input() public showReprintCommercialInvoice = true;
    @Input() public showPrintNAFTAForm = true;
    @Input() public showPrintShipmentDetails = true;
    @Input() public showViewPendingShipments = true;
    @Input() public showVoidShipment = true;
    @Input() public isAllowShipmentEdit = false;
    @Input() public showPOD = false;
    @Input() public carrier = '';
    @Input() public shipment: Package | IShipmentPackage;

    @Output() public viewClaimClicked: EventEmitter<number> = new EventEmitter<number>();
    @Output() public fileClaimClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public reshipClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public reprintLabelClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public emailLabelClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public downloadLabelClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public reprintCommercialInvoiceClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public printNAFTAFormClicked: EventEmitter<string> = new EventEmitter<string>();
    @Output() public printShipmentDetailsClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public viewPendingShipmentClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public voidShipmentClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public editShipmentClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public PODClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

    public isPackagedPickedUp = false;
    public hasTrackingScans = false;
    public hasClaim$: Observable<boolean>;
    public claimId: number;
    private ngDestroyed$ = new Subject<void>();
    public isShipDatePastSixMonths = false;
    public customer: Customer;
    public user: User;

    public constructor(private readonly trackingScanService: TrackingScanService,
                       private readonly claimService: ClaimService,
                       private readonly enumService: EnumService,
                       private readonly appState: AppState,
    ) {
        this.customer = this.appState.customer$();
        this.user = this.appState.user$();
    }

    public get isShipmentVoided(): boolean {
        return this.shipment?.Status === 3;
    }

    public ngOnChanges(changes: SimpleChanges): void {
        this.onShipmentChanged(changes.shipment);
    }

    public ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    public actionClicked(actionName: string): void {
        switch (actionName) {
            case 'viewClaim':
                this.viewClaimClicked.emit(this.claimId);
                break;
            case 'fileClaim':
                this.fileClaimClicked.emit(true);
                break;
            case 'reship':
                this.reshipClicked.emit(true);
                break;
            case 'reprintLabel':
                this.reprintLabelClicked.emit(true);
                break;
            case 'emailLabel':
                this.emailLabelClicked.emit(true);
                break;
            case 'downloadLabel':
                this.downloadLabelClicked.emit(true);
                break;
            case 'reprintCommercialInvoice':
                this.reprintCommercialInvoiceClicked.emit(true);
                break;
            case 'printNAFTAForm':
                this.printNAFTAFormClicked.emit(this.carrier);
                break;
            case 'printShipmentDetails':
                this.printShipmentDetailsClicked.emit(true);
                break;
            case 'viewPendingShipments':
                this.viewPendingShipmentClicked.emit(true);
                break;
            case 'voidShipment':
                this.voidShipmentClicked.emit(true);
                break;
            case 'editShipment':
                this.editShipmentClicked.emit(true);
                break;
            case 'downloadPOD':
                this.PODClicked.emit(true);
                break;
            default:
                break;
        }
    }

    private onShipmentChanged(change: SimpleChange): void {
        if (!change?.currentValue) {
            return;
        }

        // MV3-7282: Claim filing should be disabled if ship date is more than 6 months
        const shipDate = new Date(this.shipment?.ShipDate);
        const currentDate = new Date();
        this.isShipDatePastSixMonths = shipDate < new Date(currentDate.setMonth(currentDate.getMonth() - 6));

        this.hasClaim$ = this.claimService.getClaimDraft(this.shipment?.TrackingNumber)
                             .pipe(
                                 catchError(() => of(null)),
                                 filter(apiClaim => !!apiClaim),
                                 tap(claim => this.claimId = claim?.claimId),
                                 map(apiClaim => this.enumService.getEnumByValue(ClaimStatus, apiClaim?.claimStatus)),
                                 map(claimStatus => !FILE_CLAIM_CONFIG.claimableClaimStatuses.includes(claimStatus)),
                             );

        if (!this.shipment.UseAccessPointCode || this.isShipmentVoided) {
            return;
        }

        this.trackingScanService.getTrackingScans(this.shipment?.TrackingNumber)
            .pipe(
                takeUntil(this.ngDestroyed$),
                catchError((err) => {
                    this.isPackagedPickedUp = true;

                    console.error(err);
                    return of([]);
                }),
            )
            .subscribe(
                (trackingScans) => {
                    this.hasTrackingScans = trackingScans?.length > 0;
                    this.isPackagedPickedUp = trackingScans?.some(
                        (r: ITrackingScan) =>
                            r.statusTypeCode?.trim().toUpperCase() === 'D'
                            && r.statusCode?.trim().toUpperCase() === '2W',
                    );
                },
            );
    }

    protected readonly environment = environment;
}
